import React, { useRef, useState } from "react";
import classes from "./ListYourBusiness.module.scss";
import { useTranslation } from "react-i18next";
import { listYourBusinessHeroItems, listYourBusinessHowItWorksItems } from "./listYourBusinessData";
import CustomButton from "../../components/ui/CustomButton/CustomButton";
import startListing from "../../assets/images/free-listing/start-listing.png";
import { Form, FormInstance } from "antd";
import FormItemInput from "../../components/ui/FormItemInput/FormItemInput";
import PhoneNumberInput from "../../components/ui/PhoneNumberInput/PhoneNumberInput";
import FormItemSelect from "../../components/ui/FormItemSelect/FormItemSelect";
import AddressInput from "../../components/ui/AddressInput/AddressInput";
import FormItemCheckbox from "../../components/ui/FormItemCheckbox/FormItemCheckbox";
import axios from "axios";
import { environment } from "../../environment";
import { openErrorMessage, openSuccessMessage } from "../../store/message/messageSlice";
import { handleError } from "../../helper/errorHandler";
import useAppDispatch from "../../hooks/useAppDispatch";
import FAQ from "../../components/FAQ/FAQ";
import testimonial01 from "../../assets/images/free-listing/testimonial-01.jpg";
import starsIcon from "../../assets/images/icons/stars.svg";
import testimonial02 from "../../assets/images/free-listing/testimonial-02.jpeg";

const ListYourBusiness = () => {
  const formRef = useRef<FormInstance>(null);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [selectedCode, setSelectedCode] = useState("+30");
  const [phoneNumber, setPhoneNumber] = useState<{ value: string; isValid: boolean }>({
    value: "",
    isValid: true,
  });
  const [formatedAddress, setFormatedAddress] = useState<{ value: string; isValid: boolean }>({
    value: "",
    isValid: true,
  });

  const onFinish = async (values: any) => {
    if (!phoneNumber.value || !formatedAddress.value) {
      if (!phoneNumber.value) {
        setPhoneNumber({
          value: phoneNumber.value,
          isValid: false,
        });
      }
      if (!formatedAddress.value) {
        setFormatedAddress({
          value: formatedAddress.value,
          isValid: false,
        });
      }
      return;
    }

    const availableServices = [
      { label: "Open Gym Access", value: values.openGymAccess },
      { label: "Personal Training", value: values.personalTraining },
      { label: "Group Fitness Classes", value: values.groupFitnessClasses },
      { label: "Swimming Pool Access", value: values.swimmingPoolAccess },
      { label: "Running Club", value: values.runningClub },
      { label: "Kids Fitness Programs", value: values.kidsFitnessPrograms },
    ];

    const model = {
      businessName: values.businessName,
      name: values.name,
      email: values.email,
      phoneNumber: `${selectedCode}${phoneNumber.value}`,
      businessType: businessTypes.find((el) => el.id === values.businessType)?.label || "-",
      location: formatedAddress.value,
      availableServices: availableServices.filter((el) => el.value).map((el) => el.label),
    };

    try {
      const response = await axios.post(
        `${environment.authPath}/api/v2/website/club-pass/send-listing-business-offer`,
        model
      );
      if (response.data) {
        formRef.current?.resetFields();
        setFormatedAddress({ value: "", isValid: true });
        setPhoneNumber({ value: "", isValid: true });
        dispatch(openSuccessMessage(t("MailSentSuccessfully")));
      } else {
        dispatch(openErrorMessage(t("SOMETHING_WENT_WRONG")));
      }
    } catch (e) {
      handleError(e, dispatch, t);
    }
  };

  const onFinishFailed = async () => {
    if (!phoneNumber.value) {
      setPhoneNumber({
        value: phoneNumber.value,
        isValid: false,
      });
    }
    if (!formatedAddress.value) {
      setFormatedAddress({
        value: formatedAddress.value,
        isValid: false,
      });
    }
  };

  const businessTypes = [
    {
      id: "fitness",
      label: t("Fitness"),
    },
    {
      id: "pilates",
      label: t("PilatesStudio"),
    },
    {
      id: "yoga",
      label: t("YogaStudio"),
    },
    {
      id: "gym",
      label: t("Gym"),
    },
    {
      id: "crossfit",
      label: t("Crossfit"),
    },
    {
      id: "calisthenics",
      label: t("Calisthenics"),
    },
  ];

  const faqs: { question: string; answer: string | string[] }[] = [
    {
      question: t("HowDoesClubPassHelpMyGymGrow"),
      answer: t("ClubPassIncreasesYourGymVisibility"),
    },
    {
      question: t("CanISetCustomLimitsOnClubPassVisitors"),
      answer: t("YesYouCanSetDailyWeeklyOrMonthlyVisitLimits"),
    },
    {
      question: t("HowDoITrackMyGymPerformanceOnClubPass"),
      answer: t("YouCanMonitorYourRevenueVisitorStats"),
    },
    {
      question: t("DoIHaveControlOverPricingAndPromotions"),
      answer: t("YesYouCanCustomizePromotionsForClubPassMembers"),
    },
    {
      question: t("WhatMarketingBenefitsDoIGetWithClubPass"),
      answer: t("ClubPassActivelyPromotesYourGymThroughTargetedAds"),
    },
    {
      question: t("WhatExclusivePerksDoClubPassPartnerGymsReceive"),
      answer: [
        t("OffOnGymEquipmentExclusiveForTYPSubscribers"),
        t("BusinessNewsletterAccessPremiumGuidesToHelp"),
        t("FreeClubPassMembershipForYearForEarlyAdopters"),
      ],
    },
    {
      question: t("HowCanIFeatureMyGymInClubPassNewslettersAndCampaigns"),
      answer: t("YouCanApplyForPromotionalOpportunitiesViaYourClubPassDashboard"),
    },
    {
      question: t("CanIOfferDifferentTypesOfMembershipsThroughClubPass"),
      answer: t("YesYouCanProvideDayPassesWeeklyPlansGroupClasses"),
    },
    {
      question: t("HowDoIOptimizeMyClubPassGymProfileForBetterReach"),
      answer: t("EnsureYourProfileIncludesHighQualityImagesUpdatedClassSchedules"),
    },
    {
      question: t("CanIGetAdditionalRevenueStreamsFromClubPass"),
      answer: t("YesBesidesRegularBookingsYouCanEarnFromClubPassExclusive"),
    },
    {
      question: t("HowDoIGetMoreSignupsFromClubPass"),
      answer: [
        t("OfferSpecialDiscountsForFirstTimeVisitors"),
        t("ParticipateInClubPassMarketingCampaigns"),
        t("KeepYourProfileIUpdatedWithFreshOffers"),
        t("OptimizeYourClassScheduleToMatchPeakBookingTimes"),
      ],
    },
  ];

  return (
    <div className={classes["page"]}>
      <section className={classes["hero"]}>
        <div className="container">
          <h1 className={classes["title"]}>{t("WantToIncreaseRevenueAndAttractNewMembers")}</h1>
          <p className={classes["subtitle"]}>{t("ListYourBusinessOnClubPassToGenerate")}</p>
          <div className={classes["wrapper"]}>
            {/* add slider for mobile */}
            {listYourBusinessHeroItems.map((item) => {
              return (
                <div key={item.id} className={classes["item"]}>
                  <img src={item.imagePath} alt={item.title} />
                  <h3>{t(item.title)}</h3>
                  <p>{t(item.text)}</p>
                </div>
              );
            })}
          </div>
          <div className={classes["btn-wrapper"]}>
            <CustomButton text={t("ListMyBusiness")} type={"button"} typeStyle={"fill"} />
          </div>
        </div>
      </section>

      <section className={classes["how-it-works"]}>
        <div className="container">
          <h2 className={classes["title"]}>{t("HowDoesItWork")}</h2>
          <p className={classes["subtitle"]}>{t("JoinTheFastestGrowingFitness")}</p>
          <div className={classes["wrapper"]}>
            {listYourBusinessHowItWorksItems.map((item) => {
              return (
                <div key={item.id} className={classes["item"]}>
                  <img src={item.imagePath} alt={item.title} />
                  <div>
                    <h3>{t(item.title)}</h3>
                    <p>{t(item.text)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials section */}
      <section className={`${classes.testimonials} container`}>
        <h3 className={classes.title}>{t("WhatOurPartnersSay")}</h3>
        <p className={classes.subtitle}>
          {t("BusinessesUsingClubPassHaveSeenASignificantIncreaseInBookings")}
        </p>
        <div className={classes.wrapper}>
          <div className={classes.item}>
            <img className={classes.profile} src={testimonial01} alt={"testimonial-01"} />
            <div>
              <img src={starsIcon} alt={"Stars"} />
              <p>{t("ClubPassHasCompletelyTransformedHowWeManageOurBookings")}</p>
              <p className={classes.name}>
                {"MALCOLM M"} <span>{"Founder of MK Pilates"}</span>
              </p>
            </div>
          </div>

          <div className={classes.item}>
            <img className={classes.profile} src={testimonial02} alt={"testimonial-02"} />
            <div>
              <img src={starsIcon} alt={"Stars"} />
              <p>{t("SinceJoiningClubPassOurStudioHasGainedSteadyStream")}</p>
              <p className={classes.name}>
                {"Niki Tzortzi"} <span>{"Owner of Pilates Fitness Yoga"}</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Start listing section */}
      <section className={classes.start}>
        <img src={startListing} alt={"Start listing"} />
        <div className={classes.box}>
          <h2 className={classes.title}>{t("DeclarationOfInterestToParticipate")}</h2>
          <p className={classes.subtitle}>
            {t("SignUpInMinutesAndStartReceivingBookingsEffortlessly")}
          </p>

          <Form
            className={classes.form}
            ref={formRef}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            name="contact-information"
            layout={"vertical"}
            initialValues={{ phoneNumber: "" }}
          >
            <FormItemInput
              name={"businessName"}
              label={t("BusinessName")}
              rules={{ required: true }}
              isSettingInput={true}
              placeholder={t("EnterYourBusinessName")}
            />

            <FormItemInput
              name={"name"}
              label={t("YourName")}
              rules={{ required: true }}
              isSettingInput={true}
              placeholder={t("EnterYourName")}
            />

            <FormItemInput
              name={"email"}
              label={t("Email")}
              rules={{ required: true }}
              isSettingInput={true}
              placeholder={"you@company.com"}
            />

            <PhoneNumberInput
              selectedCode={selectedCode}
              setSelectedCode={setSelectedCode}
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
            />

            <FormItemSelect
              name={"businessType"}
              label={t("BusinessType")}
              rules={{ required: true }}
              items={businessTypes}
              isSettingInput={true}
              showSearch={true}
              placeholder={t("Select") + "..."}
            />

            <AddressInput
              formatedAddress={formatedAddress}
              setFormatedAddress={setFormatedAddress}
            />

            <div className={classes.checkBoxexGroup}>
              <FormItemCheckbox
                name={"openGymAccess"}
                label={t("OpenGymAccess")}
                rules={{ required: false }}
                isSettingInput={true}
              />

              <FormItemCheckbox
                name={"personalTraining"}
                label={t("PersonalTraining")}
                rules={{ required: false }}
                isSettingInput={true}
              />

              <FormItemCheckbox
                name={"groupFitnessClasses"}
                label={t("GroupFitnessClasses")}
                rules={{ required: false }}
                isSettingInput={true}
              />

              <FormItemCheckbox
                name={"swimmingPoolAccess"}
                label={t("SwimmingPoolAccess")}
                rules={{ required: false }}
                isSettingInput={true}
              />

              <FormItemCheckbox
                name={"runningClub"}
                label={t("RunningClub")}
                rules={{ required: false }}
                isSettingInput={true}
              />

              <FormItemCheckbox
                name={"kidsFitnessPrograms"}
                label={t("KidsFitnessPrograms")}
                rules={{ required: false }}
                isSettingInput={true}
              />
            </div>

            <CustomButton text={t("ListMyBusinessNow")} type={"submit"} typeStyle={"fill"} />
          </Form>
        </div>
      </section>

      {/* FAQ section */}
      <FAQ faqs={faqs} backgroundColor={"#F8FAFC"} />

      <section className={classes["summary"]}>
        <div className="container">
          <h2 className={classes["title"]}>{t("WhatAreYouWaitingFor")}</h2>
          <p className={classes["subtitle"]}>
            {t("PartnerWithClubPassForFreeAndStartGrowingYourBusinessToday")}
          </p>
          <div className={classes["btn-wrapper"]}>
            <CustomButton text={t("GetStarted")} type={"button"} typeStyle={"fill"} />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ListYourBusiness;
